import { Group, Text, UnstyledButton } from "@mantine/core";
import { useState, type ReactNode } from "react";

import type { Product } from "../../api/generated/model";
import { ThumbImage } from "../../ui/ThumbImage";
import { previewImageOf } from "./product-image";
import classes from "./ProductCard.module.css";

export interface ProductCardProps {
  product: Product;
  /** Opens the product's preview, as live's card does (issue 828, bug 25). */
  onOpen: () => void;
  /** Edit and Delete, in the pill over the picture on hover; none for those who may not. */
  menu?: ReactNode;
}

interface Cover {
  full: string;
  thumb: string | null;
  width: number | null;
  height: number | null;
}

/** The product's cover: live's leading image (its first product shot, else its first image; issue
 *  828, bug 25), its 512 px thumbnail first (spec teams/product-thumbs), with the image's size,
 *  whose shape the cover keeps before it loads (spec ui-perf/product-images). */
function coverOf(product: Product): Cover | null {
  const lead = previewImageOf(product);
  if (lead?.url == null) return null;
  return {
    full: lead.url,
    thumb: lead.thumb_url ?? null,
    width: lead.width ?? null,
    height: lead.height ?? null,
  };
}

/**
 * A product in the live gallery (design-system.md 3.6): its cover at its own shape, growing on
 * hover, with the actions in a pill over it; the name and two lines of tagline below. A click
 * opens live's preview. Without a cover, or when its URL no longer loads, the product's initial
 * stands in.
 */
export function ProductCard({ product, onOpen, menu }: ProductCardProps) {
  const cover = coverOf(product);
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <div className={classes.cell}>
      <article className={classes.card}>
        {/* The name comes first, so a keyboard reaches it before the hover actions. */}
        <UnstyledButton onClick={onOpen} aria-label={product.name} className={classes.footer}>
          <Text className={classes.name} lineClamp={1}>
            {product.name}
          </Text>
          {product.tagline === null ? null : (
            <Text className={classes.tagline} lineClamp={2}>
              {product.tagline}
            </Text>
          )}
        </UnstyledButton>
        <div className={classes.mediaWrap}>
          {/* The picture opens the preview too; the name below is the one button it reads. */}
          <UnstyledButton onClick={onOpen} tabIndex={-1} aria-hidden className={classes.media}>
            {cover === null || cover.full === failed ? (
              <span className={classes.initial}>{product.name.trim().charAt(0).toUpperCase()}</span>
            ) : (
              <ThumbImage
                thumb={cover.thumb}
                full={cover.full}
                alt=""
                className={classes.image}
                onLost={() => setFailed(cover.full)}
                width={cover.width}
                height={cover.height}
              />
            )}
          </UnstyledButton>
          {menu === undefined ? null : (
            <Group gap={4} wrap="nowrap" className={classes.menu}>
              {menu}
            </Group>
          )}
        </div>
      </article>
    </div>
  );
}
